import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { HealthcheckCoreService } from '@qtek/libs/healthcheck-core';
import { HTTP_BASE_URL, Message } from '@qtek/shared/models';
import { QtHttpInterceptor } from '../qtek-interceptor';

@Injectable()
export class ErrorHandlerInterceptor implements QtHttpInterceptor {
  readonly qtek = true;

  constructor(
    @Inject(HTTP_BASE_URL) private baseUrl: string,
    private matDialog: MatDialog,
    private router: Router,
    private healthcheck: HealthcheckCoreService,
    private dialogService: LazyDialogLoader
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Not a backend call, don't try to revalidate.
    // TODO(marcincichocki) use metadata instead of url when available
    if (
      !req.url.startsWith(this.baseUrl) ||
      req.url.includes('/api/v1/service/logout')
    ) {
      return next.handle(req);
    }

    // RRRR: Remove req
    return next
      .handle(req)
      .pipe(
        catchError((response: HttpErrorResponse) =>
          this.handleError(response, req)
        )
      );
  }

  handleError(response: HttpErrorResponse, restReq?: HttpRequest<any>) {
    console.log('HTTP ERROR: ', response.status, response);
    console.log('HTTP ERROR: ', response, restReq);
    switch (response.status) {
      /**
       * We don't want to use {@link ErrorHandlerInterceptor#handleOtherStatusCode}
       * for 401 status codes.
       *
       * Revalidation is handled separately in {@link Revalidateinterceptor}.
       */
      case 400: {
        this.handle400StatusCode(response);
        break;
      }

      case 401: {
        break;
      }

      case 426: {
        this.dialogService.open('selectPlan').subscribe();

        break;
      }

      case 500: {
        this.handle500StatusCode(response);

        break;
      }

      case 504:
      case 0: {
        this.handleNoConnection(response, restReq);
        break;
      }

      default: {
        this.handleOtherStatusCode(response);

        break;
      }
    }

    return throwError(response);
  }

  /**
   * Handle 400 status code error specifically for document service.
   */
  handle400StatusCode(response: HttpErrorResponse) {
    const problems = response.error?.res?.pStats?.problems;
    if (
      response.url &&
      response.url.includes('/service/doc') &&
      problems?.length > 0
    ) {
      this.saveErrorToDataLayer(response);

      const details = problems.filter((problem: any) => problem.title);

      this.dialogService
        .open('alert', {
          title: 'GNR_DOC_VALIDATION_ERR_MESSAGE',
          extendedDetails: details,
        })
        .subscribe();
    } else {
      this.handleOtherStatusCode(response);
    }
  }

  /**
   * Hadle 500 status code error specifically.
   */
  handle500StatusCode(response: HttpErrorResponse) {
    this.matDialog.closeAll();
    this.saveErrorToDataLayer(response);

    this.router.navigate(['error']);
  }

  async handleNoConnection(
    response: HttpErrorResponse,
    request: HttpRequest<any>
  ) {
    this.healthcheck.setHttpUnhealthy();
    this.saveErrorToDataLayer(response);

    if (!request.urlWithParams.includes('/api/v1/service/meta?a=ptl')) {
      return;
    }

    this.matDialog.openDialogs
      .filter(d => d.id !== 'error-dialog')
      .forEach(d => d.close());
    this.router.navigate(['error', 'network']);
  }

  /**
   * Handle all other status codes.
   *
   * @param response Server response, can be used to gather error info.
   */
  handleOtherStatusCode(response: HttpErrorResponse) {
    if (
      response.url?.includes('/api/v1/service/email/validate') ||
      response.url?.includes('/api/v1/service/external-invest')
    ) {
      // Let feature-level logic handle validation/submit errors for lead bank flows.
      return;
    }

    this.saveErrorToDataLayer(response);

    const { sts } = response.error || {};
    const errors = Object.keys(sts && sts.attrs ? sts.attrs : {}).map(key => ({
      key,
      ...sts.attrs[key],
    }));
    const main = errors.find(error => error.main) ||
      errors[0] || { msg: 'ERR_DEFAULT_MSG' };
    const details = errors.filter(error => !error.main);
    const messageKey = main.msg || main.key || 'ERR_DEFAULT_MSG';
    const messageParams =
      main.prms ||
      (main.msg
        ? []
        : [Object.keys(sts && sts.attrs ? sts.attrs : {}).join(', ')]);

    this.dialogService
      .open('alert', {
        message: new Message(messageKey, messageParams),
        details: details.map(detail => new Message(detail.msg, detail.prms)),
      })
      .subscribe();
  }

  /**
   * Send error to google tag manager
   *
   */
  saveErrorToDataLayer(response: HttpErrorResponse) {
    if ((<any>window).dataLayer) {
      (<any>window).dataLayer.push({
        event: 'error',
        event_category: 'http_error',
        event_value: response.error,
      });
    }
  }
}
